// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import AreaTypeSelector from './AreaTypeSelector';
import AddNewCustomAnalysisValue from './AddNewCustomAnalysisValue';
import LocationAnalysisValueButton from './LocationAnalysisValueButton';
import LocationAnalysisType from '../../enums/LocationAnalysisType';
import AreaSelectorInfoPopover from './AreaSelectorInfoPopover';
import SegmentedButton from '../button/SegmentedButton';
import Mixpanel, { MIXPANEL_EVENTS } from '../../helpers/Mixpanel';
import { MIN_VERTICES, seedFromAnalysis } from '../../helpers/customShape';

export const SELECTION_LIMIT = 3;

const deriveCustomValues = (selection, areaType) => {
    return [...selection].filter(v => !(areaType.VALUES_ARRAY || []).includes(v));
};

/**
 * @typedef Props
 * @property {import('../../').LocationAnalysisType | undefined} analysisType
 * @property {Set<number>} selection
 * @property {string} mapInstanceId
 * @property {Function} onClose
 * @property {boolean} [allowCustomShape]
 * @property {import('../../objects/LocationAnalysisItem').default} [locationAnalysisItem]
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {import('../../').LocationAnalysisType | undefined} currentAreaType
 * @property {Set<number>} currentSelection
 * @property {any[]} customValues
 * @property {import('../../objects/UserPreferences').default} userPreferences
 * @property {'saved' | 'radius' | 'scratch' | null} shapeMode
 *
 * @extends {BusComponent<Props, State>}
 */
class AreaSelectorPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);

        const initialType = props.analysisType || LocationAnalysisType.RADIUS;
        const initialSelection = props.selection || new Set(initialType.DEFAULT_VALUES);
        const initialCustomValues = deriveCustomValues(initialSelection, initialType);

        this.state = {
            currentAreaType: initialType,
            currentSelection: initialSelection,
            customValues: initialCustomValues,
            userPreferences: undefined,
            shapeMode: null,
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            CUSTOM_LOCATION_ANALYSIS_VALUES: this.onCustomLocationAnalysisValues,
            USER_PREFERENCES_GET_SUCCESS: this.onUserPreferencesGetSuccess,
        });

        this.emit('CUSTOM_LOCATION_ANALYSIS_VALUES_REQUEST');
        this.emit('USER_PREFERENCES_GET_REQUEST');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /** @param {import('../../objects/UserPreferences').default} userPreferences */
    onUserPreferencesGetSuccess = userPreferences => {
        this.setState({
            userPreferences,
        });
    };

    onCustomLocationAnalysisValues({ customLocationAnalysisValues }) {
        const { currentAreaType, customValues } = this.state;

        const newCustoms = customLocationAnalysisValues
            .filter(v => v.analysisTypeUnit === currentAreaType.UNIT)
            .map(v => v.value);

        // merge with existing so deselected chips stay visible
        const mergedCustomValues = Array.from(new Set([...customValues, ...newCustoms]));

        this.setState({
            customValues: mergedCustomValues,
        });
    }

    multiSelectionChange = value => {
        const newSelection = new Set(this.state.currentSelection);

        if (newSelection.has(value) && newSelection.size > 1) {
            newSelection.delete(value);
        } else if (!newSelection.has(value) && newSelection.size < SELECTION_LIMIT) {
            newSelection.add(value);
        }

        return newSelection;
    };

    onSelectionChange = value => {
        const newSelection = this.multiSelectionChange(value);
        this.setState({
            currentSelection: newSelection,
        });
    };

    onAreaTypeChange = value => {
        const { userPreferences } = this.state;
        const newType = LocationAnalysisType[value];

        if (value === LocationAnalysisType.POLYGON.ID) {
            this.setState({
                currentAreaType: newType,
                currentSelection: new Set(newType.DEFAULT_VALUES),
                customValues: [],
            });
            return;
        }

        const newSelection = userPreferences
            ? new Set(userPreferences.getSelection(value === 'DRIVING_TIME' ? 'driving' : 'radius'))
            : new Set(newType.DEFAULT_VALUES);

        const newCustomValues = deriveCustomValues(newSelection, newType);

        this.setState({
            currentAreaType: newType,
            currentSelection: newSelection,
            customValues: newCustomValues,
        });

        // Request custom values for new type after state has updated
        setTimeout(() => {
            this.emit('CUSTOM_LOCATION_ANALYSIS_VALUES_REQUEST');
        }, 0);
    };

    onApplySelection = () => {
        const { currentSelection, currentAreaType } = this.state;

        // Sort selection from smallest to largest
        const sortedSelection = [...currentSelection].sort((a, b) => a - b);

        this.emit('SET_LOCATION_ANALYSIS_TYPE_AND_SELECTION', {
            mapInstanceId: this.props.mapInstanceId,
            analysisTypeId: currentAreaType.ID,
            selection: new Set(sortedSelection),
        });

        this.bus.emit('LOCATION_ANALYSIS_MODE_STARTED', {
            mapInstanceId: this.props.mapInstanceId,
        });

        if (!this.props.analysisType || this.props.analysisType.ID !== currentAreaType.ID) {
            Mixpanel.emitUserEvent(MIXPANEL_EVENTS.SWITCH_ISOLINE_TYPE, {
                isolineType:
                    currentAreaType.ID === LocationAnalysisType.RADIUS.ID ? 'radius' : 'driving',
                selections: Array.from(currentSelection),
            });
        }

        this.bus.emit('GET_STORAGE_FACILITY_COUNT_REQUEST', {
            mapInstanceId: this.props.mapInstanceId,
        });

        this.props.onClose();
    };

    // The shape is drawn on the map for the analysis already open; a ring opens in edit mode.
    drawShape = ring => {
        this.emit('POLYGON_DRAW_REQUEST', { mapInstanceId: this.props.mapInstanceId, ring });
        this.props.onClose();
    };

    onShapeModeChange = shapeMode => this.setState({ shapeMode });

    // Back to the radius or drive time the shape replaced; the deal drops the shape.
    onClearShape = () => {
        const item = this.props.locationAnalysisItem;
        const { userPreferences } = this.state;
        let target = item.previousAnalysis;
        if (!target && !item.isPolygon) {
            target = { analysisTypeId: item.analysisTypeId, selection: item.selection };
        }
        if (!target) {
            target = {
                analysisTypeId: LocationAnalysisType.RADIUS.ID,
                selection: new Set(
                    userPreferences
                        ? userPreferences.getSelection('radius')
                        : LocationAnalysisType.RADIUS.DEFAULT_VALUES,
                ),
            };
        }
        this.emit('CUSTOM_SHAPE_CLEAR_REQUEST', {
            mapInstanceId: this.props.mapInstanceId,
            analysisTypeId: target.analysisTypeId,
            selection: target.selection,
        });
        this.props.onClose();
    };

    /**
     * Custom shape: the saved shape or the open ring to start from, or a blank
     * map. A saved shape is the default, then the ring, then drawing.
     */
    renderShapeView() {
        const item = this.props.locationAnalysisItem;
        const savedRing = item && item.ring && item.ring.length >= MIN_VERTICES ? item.ring : null;
        const seed = seedFromAnalysis(item);
        const options = [];
        if (savedRing) options.push({ value: 'saved', label: 'Saved shape' });
        if (seed && seed.ring) options.push({ value: 'radius', label: seed.label });
        if (!savedRing) options.push({ value: 'scratch', label: 'Draw from scratch' });
        const mode = options.some(o => o.value === this.state.shapeMode)
            ? this.state.shapeMode
            : options[0].value;
        const ring = mode === 'saved' ? savedRing : mode === 'radius' ? seed.ring : undefined;

        return (
            <div className="radius-selector__shape">
                {options.length > 1 && (
                    <SegmentedButton
                        key={options.map(o => o.value).join()}
                        className="radius-selector__shape-mode"
                        options={options}
                        defaultSelected={mode}
                        onChange={this.onShapeModeChange}
                    />
                )}
                {seed && !seed.ring && (
                    <span
                        className="radius-selector__shape-reason"
                        data-testid="area-selector-start-from-reason"
                    >
                        {`${seed.label} is unavailable. ${seed.reason}`}
                    </span>
                )}
                <div className="radius-selector__actions">
                    {savedRing && (
                        <button
                            className="btn-link clear-btn radius-selector__shape-clear"
                            onClick={this.onClearShape}
                            data-testid="area-selector-clear-shape"
                        >
                            Clear custom shape
                        </button>
                    )}
                    {mode === 'saved' && !item.isPolygon && (
                        <button
                            className="btn-link apply"
                            onClick={this.onApplySelection}
                            data-testid="area-selector-use-saved-shape"
                        >
                            Use shape
                        </button>
                    )}
                    <button
                        className="btn-link apply"
                        onClick={() => this.drawShape(ring)}
                        data-testid="area-selector-edit-shape"
                    >
                        {ring ? 'Edit shape' : 'Draw shape'}
                    </button>
                </div>
            </div>
        );
    }

    onClearSelection = () => {
        this.setState({
            currentSelection: new Set(),
            customValues: [],
        });
    };

    render() {
        const { mapInstanceId, onClose, allowCustomShape } = this.props;
        const { currentSelection, currentAreaType } = this.state;

        if (currentAreaType.ID === LocationAnalysisType.POLYGON.ID) {
            return (
                <div
                    className={
                        allowCustomShape ? 'radius-selector radius-selector--three' : 'radius-selector'
                    }
                >
                    <div className="radius-selector__header">
                        <AreaTypeSelector
                            areaTypeId={currentAreaType.ID}
                            mapInstanceId={mapInstanceId}
                            onAreaTypeChange={this.onAreaTypeChange}
                            allowCustomShape={allowCustomShape}
                        />
                        <button className="btn-icon radius-selector__modal-close" onClick={onClose}>
                            <i className="material-icons">close</i>
                        </button>
                    </div>
                    <div className="radius-selector__header-title">
                        <span>
                            Outline an area on the map. The analysis keeps this location as
                            its center.
                        </span>
                    </div>
                    {this.renderShapeView()}
                </div>
            );
        }

        const isSelectionAtLimit = currentSelection.size >= SELECTION_LIMIT;

        const values = [...currentAreaType.VALUES_ARRAY, ...this.state.customValues];
        const valueButtons = values
            .sort((a, b) => a - b)
            .map(value => (
                <LocationAnalysisValueButton
                    key={value}
                    value={value}
                    selection={currentSelection}
                    onSelectionChange={this.onSelectionChange}
                    analysisTypeUnit={currentAreaType.UNIT}
                    isDisabled={isSelectionAtLimit && !currentSelection.has(value)}
                />
            ));

        const isApplyDisabled = currentSelection.size === 0;
        
        const considerPendingChanges = () => {
            this.bus.emit('AREA_SELECTOR_PENDING_SELECTION', {
                selection: new Set(currentSelection),
            });
        }

        return (
            <div
                className={
                    allowCustomShape ? 'radius-selector radius-selector--three' : 'radius-selector'
                }
            >
                <div className="radius-selector__header">
                    <AreaTypeSelector
                        areaTypeId={currentAreaType.ID}
                        mapInstanceId={mapInstanceId}
                        onAreaTypeChange={this.onAreaTypeChange}
                        allowCustomShape={allowCustomShape}
                    />
                    <button className="btn-icon radius-selector__modal-close" onClick={onClose}>
                        <i className="material-icons">close</i>
                    </button>
                </div>
                <div className="radius-selector__header-title">
                    <span>Select area (up to {SELECTION_LIMIT})</span>
                    <AreaSelectorInfoPopover />
                </div>
                <div className="radius-selector__content">
                    {valueButtons}
                    <AddNewCustomAnalysisValue
                        key="add-value-button"
                        values={values}
                        analysisType={currentAreaType}
                        isDisabled={isSelectionAtLimit}
                        onBeforeOpen={considerPendingChanges}
                    />
                </div>
                <div className="radius-selector__actions">
                    <button
                        title="Clear selected values"
                        aria-label="Clear selected values"
                        className="btn-link clear-btn"
                        onClick={this.onClearSelection}
                        disabled={currentSelection.size === 0}
                    >
                        {this.props.intl.formatMessage({ id: 'clear' })}
                    </button>
                    <button
                        title="Apply selected values"
                        aria-label="Apply selected values"
                        className="btn-link apply"
                        onClick={this.onApplySelection}
                        disabled={isApplyDisabled}
                    >
                        {this.props.intl.formatMessage({ id: 'apply' })}
                    </button>
                </div>
            </div>
        );
    }
}

AreaSelectorPopup.propTypes = {
    analysisType: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    allowCustomShape: PropTypes.bool,
    locationAnalysisItem: PropTypes.object,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(AreaSelectorPopup);
